import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * `/online-payments` — the card-payment archive, and two checkout rules.
 *
 * **FB-157.** The four stat cards describe fifty rows and are labelled as the business's.
 * `GET /payments` defaults to `limit = 50`:
 *
 * ```java
 * int size = Math.min(limit == null || limit <= 0 ? 50 : limit, MAX_PAGE);
 * ```
 *
 * Legacy passes no limit and no offset, has no pagination control at all, and then computes
 *
 * ```tsx
 * const stats = { total: payments.length, paid: …, volume: payments.filter(paid).reduce(…) };
 * ```
 *
 * On the restored data that is **fifty rows of 1 355**, and the cards read:
 *
 * | Card | Legacy shows | The archive holds |
 * |---|---|---|
 * | transactions | 50 | **1 355** |
 * | paid | 45 | **1 249** |
 * | failed | 5 | **71** |
 * | volume | **810,95 €** | **26 417,25 €** |
 *
 * The volume is out by a factor of thirty-three. And the **status filter changes the totals**: with
 * *paid* selected, `stats.total` becomes the paid count and `stats.failed` becomes zero — so the cards
 * describe the filter rather than the archive, without saying so.
 *
 * **FB-158.** The search box filters the fetched fifty by order number, client-side, while the endpoint
 * accepts no search at all. So typing an order number from last month finds nothing and the page says
 * nothing found — of a payment that is there.
 *
 * **And the money is a float.** `formatCurrency(Number(amount))` and `reduce((s, p) => s + Number(p.amount))`
 * over the paid rows: §14.7 says money is integer cents, and a thousand parsed decimals is exactly the sum
 * that drifts.
 *
 * **The rows carry customer PII the table never shows.** Each payment's `requestData` holds the payer's
 * e-mail, first and last name and the card-holder name; the archive's columns show none of them. Fifty
 * payers' details reach the browser to render a table of amounts and statuses.
 */

export interface PaymentRow {
  id: number
  orderId: number | null
  orderNumber: string | null
  /** Cents, per §14.7 — the API sends a decimal string. */
  amountCents: number
  currency: string
  status: string
  /**
   * Which method the customer actually used - `card_pay`, `qr_pay`, or null when the gateway never
   * named one, which is 103 of the owner's 1 355 rows.
   *
   * `BACKEND.md` §10A.126. Read from the gateway's `selectedPaymentMethod` on settle rather than
   * from what the session asked for, because the bank's page offers the card form and the transfer
   * buttons together and the customer can change their mind.
   */
  method: string | null
  transactionId: string | null
  paymentRef: string | null
  gateway: string
  paidAt: string | null
  createdAt: string
}

export const PAGE_SIZE = 50

export const STATUSES = ['paid', 'pending', 'failed', 'cancelled', 'refunded'] as const

export type PaymentStatus = (typeof STATUSES)[number]

/**
 * The two methods the archive filters on - the owner's *"add filter for 'CardPay' and filter for
 * 'QR Pay', so we can filter them out"*.
 *
 * **Two, not three.** A row whose method the gateway never named is not a third kind of payment,
 * it is a payment that never reached one - 71 failed, 27 refunded and 5 cancelled of the owner's
 * 1 355. Those are already reachable by their **status**, which is the honest question about them,
 * and a *Neznáme* chip would invite somebody to treat the absence as a category.
 */
export const METHODS = ['card_pay', 'qr_pay'] as const

export type PaymentMethod = (typeof METHODS)[number]

/**
 * The owner's own two words for them, capitalised as they wrote them.
 *
 * *"one 'CardPay' second one 'QrPay'"* - and `QR Pay` with the space, which is how they wrote it the
 * second time, in the sentence that asked for the filter.
 */
export function methodLabel(method: string | null): string {
  if (method === 'card_pay') return 'CardPay'
  if (method === 'qr_pay') return 'QR Pay'
  // A method nobody has labelled is shown as the gateway's own word, which is what the column
  // holds: §10A.126 stores an unrecognised method lowercased rather than dropping it.
  return method === null || method === '' ? '' : method
}

/**
 * What a row with no method should read as in the table.
 *
 * **Not blank, and not a guess.** The gateway never told us, and that is a different fact from
 * *paid by card* - the archive's own promise about `responseData` is that it is the evidence, and
 * for these rows there is none.
 */
export function methodCell(row: { method: string | null; status: string }): string {
  const labelled = methodLabel(row.method)
  if (labelled !== '') return labelled
  return tt('neuvedené')
}

export function statusLabel(status: string): string {
  if (status === 'paid') return tt('Zaplatené')
  if (status === 'pending') return tt('Čaká')
  if (status === 'failed') return tt('Zlyhala')
  if (status === 'cancelled') return tt('Zrušená')
  if (status === 'refunded') return tt('Vrátená')
  // A status nobody has labelled is shown as its key, because the key is what the gateway sent.
  return status
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * Read a page of payments.
 *
 * `requestData` is deliberately not carried into the row: it holds the payer's name and e-mail, the table
 * shows neither, and a field in state is a field in every error report.
 */
export function rowsOf(payload: unknown): PaymentRow[] {
  if (!Array.isArray(payload)) return []
  return payload.map((entry) => {
    const row = entry as Record<string, unknown>
    return {
      id: typeof row.id === 'number' ? row.id : 0,
      orderId: typeof row.orderId === 'number' ? row.orderId : null,
      orderNumber: text(row.orderNumber),
      amountCents: parseMoneyToCents(typeof row.amount === 'string' ? row.amount : 0),
      currency: text(row.currency) ?? 'EUR',
      status: text(row.status) ?? '',
      method: text(row.method),
      transactionId: text(row.transactionId),
      paymentRef: text(row.paymentRef),
      gateway: text(row.gateway) ?? '',
      paidAt: text(row.paidAt),
      createdAt: text(row.createdAt) ?? '',
    }
  })
}

// ─── FB-157: what the figures describe ───────────────────────────────────────
//
// `WindowTotals`, `totalsOf`, `isWindowed`, `figureLabel`, `windowNote` and `volumeLabel` were here
// and are deleted — §9.35, 2026-08-06. They were the *first* answer to FB-157: the stat cards were
// computed from whatever page `GET /payments` happened to return, so they read 50 transactions and
// 810,95 € where the archive holds 1 355 and 26 417,25 €, and this cluster's job was to prefix every
// such figure with *najmenej* and explain the window underneath.
//
// The second answer replaced it. `GET /payments/count` reports the real totals, `archiveTotalsOf`
// reads them and the cards state them outright — so there is no window left to caveat, and the
// entry points were never called by the screen that shipped. Deleted rather than kept as a second
// way to describe the same cards: a helper that says "at least 1 355" beside a card that knows it is
// exactly 1 355 is worse than nothing.

// ─── FB-158: the search ──────────────────────────────────────────────────────

/**
 * Filter the fetched rows by order number.
 *
 * **The server searches now** (FB-158), so this is no longer the whole of it — `GET /payments?search=`
 * matches the order number or the payment reference across the archive rather than across the fifty rows
 * that happened to come back. This stays as the local narrowing while the request is in flight, so typing
 * still feels immediate; the authoritative answer replaces it when it lands.
 */
export function searchRows(rows: readonly PaymentRow[], query: string): PaymentRow[] {
  const needle = query.trim().toLowerCase()
  if (needle === '') return [...rows]
  return rows.filter((row) => (row.orderNumber ?? '').toLowerCase().includes(needle))
}

// `searchNote` was here and is deleted with them. It returned the empty string unconditionally —
// FB-158 made the server search the whole archive, so the warning it used to carry had nothing left
// to say. A function whose only behaviour is to return '' is a fossil, and its test asserted exactly
// that.

// ─── FB-157: the totals, from the server ─────────────────────────────────────

/**
 * What the archive actually holds, as `GET /payments/count` reports it.
 *
 * The four stat cards were computed from whatever the last request returned — on the restored data that
 * meant *50 transactions, 810,95 €* where the archive holds **1 355** and **26 417,25 €**, a factor of
 * thirty-three. `null` while the request is in flight, so a screen says nothing rather than a page total.
 */
export interface ArchiveTotals {
  total: number
  paidCount: number
  pendingCount: number
  failedCount: number
  refundedCount: number
  /** §10A.126 - off the same scan as the statuses, so a chip costs no extra request. */
  cardPayCount: number
  qrPayCount: number
  paidCents: number
}

export function archiveTotalsOf(payload: unknown): ArchiveTotals | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.total !== 'number') return null
  return {
    total: row.total,
    paidCount: typeof row.paidCount === 'number' ? row.paidCount : 0,
    pendingCount: typeof row.pendingCount === 'number' ? row.pendingCount : 0,
    failedCount: typeof row.failedCount === 'number' ? row.failedCount : 0,
    refundedCount: typeof row.refundedCount === 'number' ? row.refundedCount : 0,
    cardPayCount: typeof row.cardPayCount === 'number' ? row.cardPayCount : 0,
    qrPayCount: typeof row.qrPayCount === 'number' ? row.qrPayCount : 0,
    // Cents, per §14.7 — the money was a parsed float summed across a thousand rows.
    paidCents: parseMoneyToCents(typeof row.paidTotal === 'number' || typeof row.paidTotal === 'string' ? row.paidTotal : 0),
  }
}

/**
 * Whether the rows on screen are the whole of what matched.
 *
 * Answered against the server's count rather than against the page size, so a filter that genuinely
 * returns fewer than fifty is not described as a truncated window.
 */
export function isTruncated(rows: readonly PaymentRow[], totals: ArchiveTotals | null): boolean {
  return totals !== null && rows.length < totals.total
}

/** A keystroke is not a query. Long enough to stop typing, short enough not to feel stuck. */
export const SEARCH_DEBOUNCE_MS = 300

/**
 * What the figures describe, said before the figures.
 *
 * Three states, because two would hide the one that matters. The count in flight is not the same as
 * *these are all of them* — a screen that assumed the good case while waiting would state a page as a
 * total, which is the defect this whole item is about.
 */
export function archiveNote(shown: number, totals: ArchiveTotals | null): string {
  if (totals === null) {
    return tt('Načítavam, koľko transakcií archív obsahuje…')
  }
  if (shown >= totals.total) {
    return `${tt('Zobrazené sú všetky')} ${formatCount(totals.total)} ${tt('transakcie tohto výberu.')}`
  }
  return `${tt('Zobrazených je')} ${formatCount(shown)} ${tt('z')} ${formatCount(totals.total)} - ${tt('čísla vyššie sú z celého archívu, nie z tejto strany.')}`
}

// ─── The refund ──────────────────────────────────────────────────────────────

export type RefundBlock = 'not-paid' | 'no-transaction' | null

/**
 * Whether a refund can even be attempted.
 *
 * `OnlinePaymentService.refund` refuses a payment that is not `paid`, one with no transaction id, an
 * unconfigured gateway, and a **bank transfer** — *"a transfer is refunded by making another transfer; the
 * gateway has nothing to reverse."* The first two are visible in the row; the last two are not, so the button
 * is offered and the reason arrives from the server.
 */
export function refundBlock(row: PaymentRow): RefundBlock {
  if (row.status !== 'paid') return 'not-paid'
  if (row.transactionId === null) return 'no-transaction'
  return null
}

export function refundBlockMessage(block: RefundBlock): string {
  if (block === 'not-paid') return tt('Vrátiť sa dá len zaplatená transakcia.')
  if (block === 'no-transaction') return tt('Bez Transaction ID sa vrátenie nedá vykonať.')
  return ''
}

export function refundConfirmation(row: PaymentRow): string {
  const reference = row.orderNumber ?? String(row.id)
  return `${tt('Objednávka')} ${reference}: ${formatCents(row.amountCents)}. ${tt('Peniaze idú zákazníkovi späť cez banku a vrátenie sa nedá zrušiť. Prevod (nie kartu) banka vrátiť nevie - to server odmietne.')}`
}

// ─── The two checkout rules ──────────────────────────────────────────────────

export interface CheckoutPrefs {
  defaultOnline: boolean
  allowOnlinePickup: boolean
}

export function prefsOf(settings: Record<string, unknown> | undefined): CheckoutPrefs {
  return {
    defaultOnline: settings?.defaultOnline === true,
    allowOnlinePickup: settings?.allowOnlinePickup === true,
  }
}

/** Only the two flags this page owns. The gateway credentials belong to the Control Tower. */
export function prefsPayload(prefs: CheckoutPrefs): Record<string, unknown> {
  return { defaultOnline: prefs.defaultOnline, allowOnlinePickup: prefs.allowOnlinePickup }
}

export type GatewayState = 'live' | 'test' | 'inactive' | 'unconfigured'

export function gatewayState(settings: Record<string, unknown> | undefined): GatewayState {
  if (settings === undefined) return 'unconfigured'
  if (settings._hasClientSecret !== true) return 'unconfigured'
  if (settings.isActive !== true) return 'inactive'
  return settings.testMode === true ? 'test' : 'live'
}

export function gatewayMessage(state: GatewayState, gateway: string): string {
  if (state === 'unconfigured') {
    return tt('Platobná brána nie je nakonfigurovaná - karta sa v pokladni neponúkne.')
  }
  if (state === 'inactive') return tt('Brána je nakonfigurovaná, ale vypnutá.')
  if (state === 'test') {
    return `${gateway}: ${tt('testovací režim - platby nie sú skutočné.')}`
  }
  return `${gateway}: ${tt('ostrý režim.')}`
}
